/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Table,
  Tooltip,
  notification,
  Pagination,
} from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Button as CrceButton, Spinner } from '@crc-evans/ui-components';
import {
  Beaker,
  Layers,
  Pencil,
  Plus,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import axiosConfig from '../../services/axiosConfig';
import './ConsumableMaterialAdmin.css';
import '../../pages/technical/HeatProfilePage.css';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';

const { Option } = Select;

const MATERIAL_TYPES = ['coating', 'welding'];

const CM_FACET_KEYS = ['category', 'name', 'productCode', 'unit', 'materialType', 'status'];

const CM_COLUMN_FILTERS_EMPTY = {
  sno: '',
  category: '',
  name: '',
  productCode: '',
  unit: '',
  materialType: '',
  status: '',
};

/* ─────────────────────────────────────────────────────────────────────────────
   Generic master admin page – used for both Consumable and Material
───────────────────────────────────────────────────────────────────────────── */
const MasterAdmin = ({ endpoint, title }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [columnFilters, setColumnFilters] = useState(() => ({ ...CM_COLUMN_FILTERS_EMPTY }));
  const [sortKey, setSortKey] = useState('category');
  const [sortDesc, setSortDesc] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();

  /* ── Fetch list ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosConfig.get(`/${endpoint}/all`);
      const items = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(items) ? items : [];
      setData(list.map((item, i) => ({ ...item, sno: i + 1 })));
    } catch {
      notification.error({ message: `Failed to load ${title} list` });
    } finally {
      setLoading(false);
    }
  }, [endpoint, title]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const cmDataFacetRef = useRef([]);
  cmDataFacetRef.current = data || [];
  const fetchCmFacetRows = useCallback(async () => {
    const list = cmDataFacetRef.current;
    return Array.isArray(list) ? list : [];
  }, []);

  const searchCriteriaCmFacets = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: true,
    filterableColumns: CM_FACET_KEYS,
    listCount: (data || []).length,
    projectList: data || [],
    searchCriteria: searchCriteriaCmFacets,
    columnFilters,
    onColumnFilterChange: handleColumnFilterChange,
    fetchFacetRows: fetchCmFacetRows,
  });

  const handleSortClick = useCallback((field) => {
    setSortKey((prev) => {
      if (prev === field) {
        setSortDesc((d) => !d);
        return prev;
      }
      setSortDesc(false);
      return field;
    });
  }, []);

  /* ── PUT { status } ── */
  const handleStatusChange = useCallback(
    async (record, nextStatus) => {
      if (!record?.id) return;
      setStatusUpdatingId(record.id);
      try {
        await axiosConfig.put(`/${endpoint}/${record.id}`, { status: nextStatus });
        notification.success({
          message: `${title} marked as ${nextStatus === 'active' ? 'Active' : 'Inactive'}`,
        });
        await fetchData();
      } catch {
        notification.error({ message: `Failed to update ${title} status` });
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [endpoint, title, fetchData]
  );

  /* ── Open Add ── */
  const handleAdd = useCallback(() => {
    setEditRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setModalVisible(true);
  }, [form]);

  /* ── Open Edit ── */
  const handleEdit = useCallback(
    (record) => {
      setEditRecord(record);
      form.setFieldsValue({
        category: record.category ?? '',
        name: record.name ?? '',
        productCode: record.productCode ?? '',
        unit: record.unit ?? '',
        materialType: record.materialType ?? undefined,
        status: (record.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
      });
      setModalVisible(true);
    },
    [form]
  );

  /* ── Save (create or update) ── */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editRecord) {
        await axiosConfig.put(`/${endpoint}/${editRecord.id}`, values);
        notification.success({ message: `${title} updated successfully` });
      } else {
        await axiosConfig.post(`/${endpoint}`, values);
        notification.success({ message: `${title} added successfully` });
      }
      setModalVisible(false);
      form.resetFields();
      setEditRecord(null);
      await fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: `Failed to save ${title}` });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditRecord(null);
  };

  const filteredAndFaceted = useMemo(() => {
    let list = [...(data || [])];
    const lc = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

    if (columnFilters.sno.trim()) {
      const q = columnFilters.sno.trim();
      list = list.filter((r) => String(r.sno ?? '').includes(q));
    }
    ['category', 'name', 'productCode', 'unit', 'materialType', 'status'].forEach((key) => {
      const raw = (columnFilters[key] || '').trim();
      if (!raw) return;
      const q = lc(raw);
      list = list.filter((r) => {
        if (key === 'status') {
          const norm = (r.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active';
          return norm.includes(q);
        }
        return lc(r[key]).includes(q);
      });
    });

    return applyFacetClientFilter(list);
  }, [data, columnFilters, applyFacetClientFilter]);

  const sortedList = useMemo(() => {
    const list = [...filteredAndFaceted];
    list.sort((a, b) => {
      if (sortKey === 'sno') {
        const va = Number(a.sno) || 0;
        const vb = Number(b.sno) || 0;
        return sortDesc ? vb - va : va - vb;
      }
      const va = (a[sortKey] ?? '').toString();
      const vb = (b[sortKey] ?? '').toString();
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });
    return list;
  }, [filteredAndFaceted, sortKey, sortDesc]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(sortedList.length / pageSize) || 1);
    setPage((p) => (p > lastPage ? lastPage : p));
  }, [sortedList.length, pageSize]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedList.slice(start, start + pageSize);
  }, [sortedList, page, pageSize]);

  const renderSortCaret = useCallback(
    (field) =>
      sortKey === field ? (
        sortDesc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [sortKey, sortDesc]
  );

  const renderCmTextColumnTitle = useCallback(
    (label, dataIndex, sortField, showFacet) => (
      <div className="column-header-with-filter">
        <div className="column-header-text-wrapper spares-column-header-sortable">
          <span>{label}</span>
          <span
            className="custom-sort-icon"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleSortClick(sortField);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSortClick(sortField);
              }
            }}
          >
            {renderSortCaret(sortField)}
          </span>
          {showFacet ? renderTextColumnFilterTrigger(dataIndex) : null}
        </div>
        <div className="header-filter-input user-mgmt-header-search">
          <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
            <Input
              placeholder="Search"
              value={columnFilters[dataIndex] ?? ''}
              onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>
    ),
    [
      columnFilters,
      handleColumnFilterChange,
      handleSortClick,
      renderSortCaret,
      renderTextColumnFilterTrigger,
    ]
  );

  /* ── Table columns (Heat-style headers + Spares-style pager) ── */
  const columns = useMemo(
    () => [
      {
        title: renderCmTextColumnTitle('S.No', 'sno', 'sno', false),
        dataIndex: 'sno',
        key: 'sno',
        width: 88,
        align: 'center',
        render: (_, __, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: renderCmTextColumnTitle('Category', 'category', 'category', true),
        dataIndex: 'category',
        key: 'category',
        width: 160,
      },
      {
        title: renderCmTextColumnTitle('Name', 'name', 'name', true),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
      },
      {
        title: renderCmTextColumnTitle('Product Code', 'productCode', 'productCode', true),
        dataIndex: 'productCode',
        key: 'productCode',
        width: 180,
      },
      {
        title: renderCmTextColumnTitle('Unit', 'unit', 'unit', true),
        dataIndex: 'unit',
        key: 'unit',
        width: 120,
        render: (val) => val || '—',
      },
      {
        title: renderCmTextColumnTitle('Material Type', 'materialType', 'materialType', true),
        dataIndex: 'materialType',
        key: 'materialType',
        width: 160,
        render: (val) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '—'),
      },
      {
        title: renderCmTextColumnTitle('Status', 'status', 'status', true),
        dataIndex: 'status',
        key: 'status',
        width: 140,
        align: 'center',
        render: (val) => {
          const isActive = (val || 'active').toLowerCase() === 'active';
          return (
            <span
              className={`cm-status-badge ${
                isActive ? 'cm-status-badge--active' : 'cm-status-badge--inactive'
              }`}
            >
              <span
                className={`cm-status-dot ${
                  isActive ? 'cm-status-dot--active' : 'cm-status-dot--inactive'
                }`}
              />
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        title: <span className="equipment-type-column-title">Actions</span>,
        key: 'actions',
        width: 100,
        align: 'center',
        fixed: 'right',
        render: (_, record) => {
          const isActive = (record.status || 'active').toLowerCase() === 'active';
          const isUpdating = statusUpdatingId === record.id;
          return (
            <div className="cm-actions-cell">
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="cm-action-btn cm-action-edit"
                  onClick={() => handleEdit(record)}
                >
                  <Pencil size={16} strokeWidth={2} />
                </button>
              </Tooltip>
              <Tooltip title={isActive ? 'Make Inactive' : 'Make Active'}>
                <button
                  type="button"
                  className="cm-action-btn cm-action-toggle"
                  disabled={isUpdating}
                  onClick={() =>
                    handleStatusChange(record, isActive ? 'inactive' : 'active')
                  }
                >
                  {isActive ? (
                    <ToggleRight size={18} strokeWidth={2} />
                  ) : (
                    <ToggleLeft size={18} strokeWidth={2} />
                  )}
                </button>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [
      page,
      pageSize,
      handleEdit,
      handleStatusChange,
      statusUpdatingId,
      renderCmTextColumnTitle,
    ]
  );

  const headerIcon = title === 'Material' ? Layers : Beaker;
  const HeaderIcon = headerIcon;

  return (
    <div className="modern-cm-page">
      <div className="heat-profile-content-wrapper">
        <div className="heat-profile-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div
                className="heat-profile-icon-wrap"
                style={
                  title === 'Material'
                    ? { backgroundColor: '#f0fdf4', color: '#15803d' }
                    : { backgroundColor: '#eff6ff', color: '#2563eb' }
                }
              >
                <HeaderIcon size={22} color="currentColor" strokeWidth={2} />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">{title} Master</h3>
                </div>
                <p className="heat-profile-list-description">
                  Manage {title.toLowerCase()} items, categories and status
                </p>
              </div>
            </div>
            <div className="heat-profile-header-actions calibration-modern-actions">
              <CrceButton
                label={`Add ${title}`}
                variant="primary"
                size="small"
                icon={Plus}
                onClick={handleAdd}
              />
            </div>
          </div>

          <div className="spares-list-table-pager-wrap pipeline-table cm-table-pager-wrap">
            <div className="heat-profile-table-wrap">
              <Table
                className="heat-profile-table spares-list-table h-100 equipPaginationTable equipment-types-table doc-control-users-table"
                rowKey={(r) => String(r.id ?? `cm-${r.sno}-${r.productCode || ''}`)}
                columns={columns}
                dataSource={pagedData}
                loading={{
                  spinning: loading,
                  indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
                }}
                size="middle"
                scroll={{ x: 1100 }}
                pagination={false}
              />
            </div>
            {sortedList.length > 0 && (
              <div className="spares-list-pager-outer">
                <Pagination
                  className="heat-profile-pagination"
                  current={page}
                  pageSize={pageSize}
                  total={sortedList.length}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  onChange={(p, size) => {
                    setPage(p);
                    if (size != null) setPageSize(size);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalVisible && (
        <div className="cm-modal-backdrop">
          <div
            className="cm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cm-modal-head">
              <h3 className="cm-modal-title">
                {editRecord ? `Edit ${title}` : `Add ${title}`}
              </h3>
              <button
                type="button"
                className="cm-modal-close"
                onClick={handleCancel}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="cm-modal-body">
              <Form form={form} layout="vertical" autoComplete="off" name={`${endpoint}-form`}>
                <div className="cm-modal-form-grid">
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Category <span className="required">*</span></label>
                      <Form.Item
                        name="category"
                        rules={[{ required: true, message: 'Category is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter category" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Name <span className="required">*</span></label>
                      <Form.Item
                        name="name"
                        rules={[{ required: true, message: 'Name is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter name" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Product Code <span className="required">*</span></label>
                      <Form.Item
                        name="productCode"
                        rules={[{ required: true, message: 'Product code is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter product code" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Unit</label>
                      <Form.Item name="unit" noStyle>
                        <Input className="cm-modal-input" placeholder="e.g. Kg, Bag (25Kg)" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Material Type <span className="required">*</span></label>
                      <Form.Item
                        name="materialType"
                        rules={[{ required: true, message: 'Material type is required' }]}
                        noStyle
                      >
                        <Select
                          className="cm-modal-select"
                          placeholder="Select material type"
                          dropdownClassName="heat-profile-select-dropdown"
                        >
                          {MATERIAL_TYPES.map((t) => (
                            <Option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Status <span className="required">*</span></label>
                      <Form.Item
                        name="status"
                        rules={[{ required: true, message: 'Status is required' }]}
                        noStyle
                      >
                        <Select
                          className="cm-modal-select"
                          placeholder="Select status"
                          dropdownClassName="heat-profile-select-dropdown"
                        >
                          <Option value="active">Active</Option>
                          <Option value="inactive">Inactive</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="cm-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
              <CrceButton
                label={saving ? 'Saving...' : editRecord ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Named exports – each used as a separate top-level admin tab
───────────────────────────────────────────────────────────────────────────── */
export const ConsumableMasterAdmin = () => (
  <MasterAdmin endpoint="consumable-master" title="Consumable" />
);

export const MaterialMasterAdmin = () => (
  <MasterAdmin endpoint="material-master" title="Material" />
);
